import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { SQRT3, linesForCells, sizeFromCellCount, v } from '../constants';
import { applyModifier, badRestrictionCells, checkRestriction, checkSolved, computeConstraintStatuses } from '../rules';
import { gameReducer, type GameState } from '../state';
import { BagTile, HexBoard, type DragState } from './HexBoard';
import { Bag } from './Bag';
import { MarkLegend, Wordmark } from './ui';
import { SharedHeader } from './SharedHeader';
import { Haptics } from '../lib/haptics';
import {
  castVote, clearCustomProgress, clearVote, deleteCustomPuzzle, fetchCustomPuzzle, fetchLeaderboard, fetchMyVote,
  loadCustomProgress, recordCustomSolve, reportCustomPuzzle, saveCustomProgress,
  type CustomPuzzleFetch, type LeaderboardEntry, type VoteValue,
} from '../lib/customPuzzles';
import { useAuth } from '../auth/AuthContext';

// Full-page play surface for a shared `/p/:id` puzzle. Mounted by
// main.tsx OUTSIDE the AuthProvider — this route should work for
// anonymous visitors with the link. Reads via fetchCustomPuzzle, plays
// with a local reducer (no global game state interference), POSTs to
// /custom/:id/solves on completion (anonymous-friendly server-side).

const DRAG_THRESHOLD = 5;

interface CustomPuzzlePlayProps {
  id: string;
}

type Phase = 'loading' | 'not_found' | 'ready';

export function CustomPuzzlePlay({ id }: CustomPuzzlePlayProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [meta, setMeta] = useState<CustomPuzzleFetch | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchCustomPuzzle(id);
      if (cancelled) return;
      if (!data) { setPhase('not_found'); return; }
      setMeta(data);
      setPhase('ready');
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (phase === 'loading') {
    return (
      <div className="hm-app hm-custom-app">
        <SharedHeader activeView="play" trailingNav={[{ label: '← Browse', href: '/browse' }]} />
        <CenteredMessage title="Loading…" />
      </div>
    );
  }
  if (phase === 'not_found' || !meta) {
    return (
      <div className="hm-app hm-custom-app">
        <SharedHeader activeView="play" trailingNav={[{ label: '← Browse', href: '/browse' }]} />
        <CenteredMessage
          title="Puzzle not found"
          body="This share link may have been removed, or never existed. Head back to today's puzzle."
          showHomeLink
        />
      </div>
    );
  }
  return <PlaySurface id={id} meta={meta} />;
}

// ─────────────────────────────────────────────────────────────────────

function CenteredMessage({
  title, body, showHomeLink,
}: {
  title: string;
  body?: string;
  showHomeLink?: boolean;
}) {
  return (
    <div
      style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 24px', textAlign: 'center', gap: 14,
      }}
    >
      <div style={{ marginBottom: 8 }}><Wordmark /></div>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{title}</h1>
      {body && <p style={{ margin: 0, maxWidth: 380, color: v('inkSoft'), fontSize: 14 }}>{body}</p>}
      {showHomeLink && (
        <a href="/" className="hm-primary" style={{ marginTop: 12, padding: '8px 18px', textDecoration: 'none' }}>
          Open Hexponents
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

// Per-puzzle localStorage key for the timer start. Lets the elapsed clock
// survive a refresh; cleared on solve and on reset.
function startedAtKey(id: string): string { return `hm.custom.startedAt.${id}`; }

function makeStateFor(meta: CustomPuzzleFetch): GameState {
  // Adapt the fetched payload into the same GameState shape the daily
  // game uses. `puzzleDate` is a sentinel — custom puzzles aren't on the
  // daily calendar, so `dateFromPuzzleDate` machinery doesn't apply.
  //
  // Restore an earlier timer-start if we have one from this same session;
  // otherwise stamp a fresh `now` and persist it so refreshing keeps the
  // clock continuous. Cleared on reset / solve.
  let startedAt = Date.now();
  try {
    const saved = localStorage.getItem(startedAtKey(meta.id));
    const n = saved ? parseInt(saved, 10) : NaN;
    // Guard against bogus / very-stale values (older than 24h => discard
    // so the user doesn't return weeks later to a 412-hour timer).
    if (Number.isFinite(n) && Date.now() - n < 24 * 60 * 60 * 1000) {
      startedAt = n;
    } else {
      localStorage.setItem(startedAtKey(meta.id), String(startedAt));
    }
  } catch { /* localStorage unavailable — just use a fresh start */ }
  return {
    difficulty: 'Easy',
    puzzleDate: 'custom',
    name: meta.payload.name,
    cells: meta.payload.cells.map(c => ({ ...c })),
    bag: meta.payload.bag.map(t => ({ ...t, placed: false })),
    constraints: meta.payload.constraints,
    selectedTileId: null,
    history: [],
    startedAt,
    solved: false,
    elapsedAtSolve: 0,
    recentlyPlaced: null,
  };
}

function PlaySurface({ id, meta }: { id: string; meta: CustomPuzzleFetch }) {
  const auth = useAuth();
  const [state, dispatch] = useReducer(gameReducer, meta, makeStateFor);
  const [now, setNow] = useState(Date.now());
  const [winDismissed, setWinDismissed] = useState(false);
  const solveSentRef = useRef(false);
  // Most-recent server snapshot, used to skip identical PUTs.
  const lastSavedRef = useRef<string>('');

  // Phase 3 — votes, leaderboard, report.
  const [thumbsUp, setThumbsUp] = useState(meta.thumbsUp);
  const [thumbsDown, setThumbsDown] = useState(meta.thumbsDown);
  const [myVote, setMyVote] = useState<VoteValue | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showReport, setShowReport] = useState(false);

  // Pull the user's existing vote (if signed in) once on mount.
  useEffect(() => {
    if (!auth.user) return;
    (async () => {
      const v = await fetchMyVote(id);
      setMyVote(v);
    })();
  }, [id, auth.user]);

  // Load server-stored progress on mount / when auth flips. Anonymous
  // players keep their state in localStorage only.
  useEffect(() => {
    if (!auth.user) return;
    let cancelled = false;
    (async () => {
      const prog = await loadCustomProgress(id);
      if (cancelled || !prog) return;
      if (!Array.isArray(prog.cells) || !Array.isArray(prog.bag)) return;
      // Layer the snapshot on top of the fresh state. `LOAD_PROGRESS`
      // keeps constraints/name intact and just swaps cells+bag.
      dispatch({
        type: 'LOAD_PROGRESS',
        cells: prog.cells,
        bag: prog.bag,
        solved: prog.solved,
        elapsedMs: prog.elapsedMs ?? null,
      });
      // If the puzzle was solved before, the elapsed-time localStorage
      // entry is stale — wipe it so we don't reset the clock to "now"
      // on a re-solve.
      if (prog.solved) {
        try { localStorage.removeItem(startedAtKey(id)); } catch { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [id, auth.user]);

  // Save progress on every cells/bag change, debounced. Signed-in only;
  // anonymous keeps its progress in localStorage (just startedAt today,
  // not the full snapshot — that's a future addition).
  useEffect(() => {
    if (!auth.user) return;
    const snapshot = JSON.stringify({ cells: state.cells, bag: state.bag, solved: state.solved });
    if (snapshot === lastSavedRef.current) return;
    const handle = setTimeout(() => {
      saveCustomProgress(id, {
        cells: state.cells,
        bag: state.bag,
        solved: state.solved,
        elapsedMs: state.solved ? state.elapsedAtSolve : undefined,
      }).then(ok => { if (ok) lastSavedRef.current = snapshot; })
        .catch(() => { /* swallow — next change retries */ });
    }, 600);
    return () => clearTimeout(handle);
  }, [id, auth.user, state.cells, state.bag, state.solved, state.elapsedAtSolve]);

  async function handleVote(value: VoteValue) {
    if (!auth.user) {
      alert('Sign in to vote on puzzles.');
      return;
    }
    // Clicking the active vote clears it; clicking the other side flips
    // it. The server is the source of truth for the counter deltas.
    const isClearing = myVote === value;
    const r = isClearing ? await clearVote(id) : await castVote(id, value);
    if (r.ok) {
      setMyVote(isClearing ? null : (r.vote ?? value));
      if (typeof r.thumbsUp === 'number') setThumbsUp(r.thumbsUp);
      if (typeof r.thumbsDown === 'number') setThumbsDown(r.thumbsDown);
    } else if (r.error === 'cant_vote_own') {
      alert("You can't vote on a puzzle you authored.");
    }
  }

  // Ticking clock — pause on solve.
  useEffect(() => {
    if (state.solved) return;
    const iv = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(iv);
  }, [state.solved, state.startedAt]);
  const elapsed = state.solved ? state.elapsedAtSolve : now - state.startedAt;

  const evaluatedConstraints = useMemo(
    () => computeConstraintStatuses(state.cells, state.constraints),
    [state.cells, state.constraints],
  );
  const badRestr = useMemo(() => badRestrictionCells(state.cells), [state.cells]);

  // Win detection — mirrors App.tsx: on transition to solved, mark +
  // POST to the server (idempotent, anonymous-friendly).
  useEffect(() => {
    if (state.solved) return;
    if (state.bag.length === 0) return;
    if (checkSolved(state.cells, evaluatedConstraints, state.bag)) {
      const ms = Date.now() - state.startedAt;
      dispatch({ type: 'MARK_SOLVED', elapsed: ms });
      Haptics.solved();
      try { localStorage.removeItem(startedAtKey(id)); } catch { /* ignore */ }
      if (!solveSentRef.current) {
        solveSentRef.current = true;
        // Fire-and-forget — recordCustomSolve never throws.
        recordCustomSolve(id, ms);
      }
    }
  }, [id, state.cells, state.bag, state.solved, state.startedAt, evaluatedConstraints]);

  // Clear placement-flash highlight after the animation runs.
  useEffect(() => {
    if (state.recentlyPlaced == null) return;
    const t = setTimeout(() => dispatch({ type: 'CLEAR_PLACE_HIGHLIGHT' }), 320);
    return () => clearTimeout(t);
  }, [state.recentlyPlaced]);

  // ─────────────── Drag state (mirrors App.tsx + Tutorial.tsx) ───────
  const [drag, setDrag] = useState<DragState | null>(null);

  const handleTilePointerDown = useCallback(
    (tile: { id: string; value: number; placed?: boolean }, e: React.PointerEvent) => {
      if (state.solved) return;
      if (tile.placed) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      setDrag({
        type: 'bag',
        tileId: tile.id,
        value: tile.value,
        startX: e.clientX, startY: e.clientY,
        x: e.clientX, y: e.clientY,
        active: false,
        overIdx: null,
        overBag: false,
      });
    },
    [state.solved],
  );

  const handleCellPointerDown = useCallback(
    (cellIdx: number, e: React.PointerEvent) => {
      if (state.solved) return;
      const cell = state.cells[cellIdx];
      if (!cell || cell.kind !== 'placed') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      setDrag({
        type: 'cell',
        cellIdx,
        value: cell.modifier != null && cell.value != null
          ? (applyModifier(cell.value, cell.modifier) ?? undefined)
          : cell.value,
        rawValue: cell.value,
        startX: e.clientX, startY: e.clientY,
        x: e.clientX, y: e.clientY,
        active: false,
        overIdx: null,
        overBag: false,
      });
    },
    [state.solved, state.cells],
  );

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const active = drag.active || dist > DRAG_THRESHOLD;
      let overIdx: number | null = null;
      let overBag = false;
      if (active) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) overIdx = idx;
        } else if (target && (target as Element).closest && (target as Element).closest('[data-bag-drop]')) {
          overBag = true;
        }
      }
      setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY, active, overIdx, overBag } : null);
    };
    const onUp = (e: PointerEvent) => {
      const wasActive = drag.active ||
        Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > DRAG_THRESHOLD;
      if (wasActive) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        const bagEl = target && (target as Element).closest && (target as Element).closest('[data-bag-drop]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) {
            if (drag.type === 'bag' && drag.tileId) {
              dispatch({ type: 'DRAG_FROM_BAG', tileId: drag.tileId, cellIdx: idx });
              Haptics.tilePlaced();
            } else if (drag.type === 'cell' && drag.cellIdx != null && idx !== drag.cellIdx) {
              dispatch({ type: 'MOVE_CELL', fromIdx: drag.cellIdx, toIdx: idx });
              Haptics.tilePlaced();
            }
          }
        } else if (bagEl && drag.type === 'cell' && drag.cellIdx != null) {
          dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: drag.cellIdx });
          Haptics.tilePicked();
        }
        const suppress = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
        document.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => {
          document.removeEventListener('click', suppress, { capture: true });
        }, 50);
      }
      setDrag(null);
    };
    const onCancel = () => setDrag(null);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
  }, [drag]);

  // ─────────────── Click handlers (tap-to-place fallback) ────────────
  const [focusedCellIdx, setFocusedCellIdx] = useState<number | null>(null);
  const handleSelectTile = (tileId: string) => {
    setFocusedCellIdx(null);
    dispatch({ type: 'SELECT_TILE', tileId });
  };
  const handleCellClick = (i: number) => {
    if (state.selectedTileId) {
      setFocusedCellIdx(null);
      dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: i });
      Haptics.tilePlaced();
      return;
    }
    const cell = state.cells[i];
    if (cell.kind === 'placed') {
      setFocusedCellIdx(null);
      dispatch({ type: 'PLACE_OR_PICKUP', cellIdx: i });
      Haptics.tilePicked();
      return;
    }
    if (cell.kind === 'gap') return;
    setFocusedCellIdx(prev => (prev === i ? null : i));
  };

  // Bag-tile highlight derived from the focused cell — categorical
  // constraints + the cell's own restriction. Mirrors the App.tsx
  // path; sum/product don't drive highlights since they need a
  // partial-line solver.
  const highlightedTileIds = useMemo<Set<string>>(() => {
    if (focusedCellIdx == null) return new Set();
    const set = new Set<string>();
    const lines = linesForCells(state.cells.length);
    const focusedConstraints = evaluatedConstraints.filter(c =>
      lines[c.axis][c.index].includes(focusedCellIdx),
    );
    const focusedCell = state.cells[focusedCellIdx];
    const restriction = focusedCell?.kind === 'empty' ? (focusedCell._restriction ?? null) : null;
    const matches = (val: number, type: string, val2?: number | number[]): boolean => {
      switch (type) {
        case 'allEven': return val % 2 === 0;
        case 'allOdd':  return Math.abs(val % 2) === 1;
        case 'noNeg':   return val >= 0;
        case 'allNeg':  return val < 0;
        case 'contains': return Array.isArray(val2) && val2.includes(val);
        default: return false;
      }
    };
    state.bag.forEach(t => {
      if (t.placed) return;
      if (restriction && !checkRestriction(t.value, restriction)) return;
      for (const c of focusedConstraints) {
        if (matches(t.value, c.type, c.value)) { set.add(t.id); return; }
      }
      if (focusedConstraints.length === 0 && restriction) set.add(t.id);
    });
    return set;
  }, [focusedCellIdx, evaluatedConstraints, state.bag, state.cells]);

  const clickableCells = useMemo(() => {
    const set = new Set<number>();
    state.cells.forEach((c, i) => {
      // Every non-gap cell is clickable. Placed → pickup, empty + tile
      // selected → place, anything else → toggle focus highlight.
      if (c.kind !== 'gap') set.add(i);
    });
    return set;
  }, [state.cells]);

  const draggableCells = useMemo(() => {
    const set = new Set<number>();
    state.cells.forEach((c, i) => { if (c.kind === 'placed') set.add(i); });
    return set;
  }, [state.cells]);

  // ─────────────── Board sizing ──────────────────────────────────────
  const boardWrapRef = useRef<HTMLDivElement | null>(null);
  const [boardBox, setBoardBox] = useState({ w: 480, h: 360 });
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setVw(window.innerWidth);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  useEffect(() => {
    if (!boardWrapRef.current || typeof ResizeObserver === 'undefined') return;
    const el = boardWrapRef.current;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setBoardBox({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { boardR, boardPad } = useMemo(() => {
    const size = sizeFromCellCount(state.cells.length);
    const numColsM1 = 2 * size - 2;
    const widthCoef = 3 * size - 1;
    const heightCoef = (2 * size - 1) * SQRT3;
    const minR = vw < 480 ? 22 : 28;
    const maxR = vw < 480 ? 36 : vw < 720 ? 48 : 60;
    const padW = vw < 480 ? 70 : 78;
    const padH = vw < 480 ? 40 : 56;
    const gap = 5;
    const Rw = (boardBox.w - 2 * padW - numColsM1 * gap) / widthCoef;
    const Rh = (boardBox.h - 2 * padH - numColsM1 * gap) / heightCoef;
    let R = Math.min(Rw, Rh, maxR);
    if (!Number.isFinite(R) || R < minR) R = minR;
    return { boardR: R, boardPad: Math.max(padH, padW) };
  }, [boardBox, vw, state.cells.length]);

  // ─────────────── Render ────────────────────────────────────────────
  const tilesPlaced = state.bag.filter(t => t.placed).length;
  const linesOk = evaluatedConstraints.filter(c => c.status === 'ok').length;
  const totalLines = evaluatedConstraints.length;

  return (
    <div className="hm-app hm-custom-app">
      <SharedHeader activeView="play" trailingNav={[{ label: '← Browse', href: '/browse' }]} />

      <div className="hm-main">
        <aside className="hm-rail" aria-label="Puzzle controls">
          <div className="hm-rail-meta">
            <div className="hm-eyebrow">Custom puzzle</div>
            <div className="hm-rail-title">{meta.payload.name}</div>
            {meta.author?.displayName && (
              <div style={{ fontSize: 12, color: v('inkMute'), marginTop: 2 }}>
                by{' '}
                <a
                  href={`/u/${encodeURIComponent(meta.author.displayName)}`}
                  style={{ color: v('inkSoft'), fontWeight: 600, textDecoration: 'none' }}
                >
                  {meta.author.displayName}
                </a>
              </div>
            )}
          </div>

          <div className="hm-rail-status">
            <div className="hm-eyebrow">Status</div>
            <div className="hm-status-row">
              <span><span className="hm-status-long">Lines passing</span><span className="hm-status-short">Lines</span></span>
              <span>{linesOk} / {totalLines}</span>
            </div>
            <div className="hm-status-row">
              <span><span className="hm-status-long">Tiles placed</span><span className="hm-status-short">Tiles</span></span>
              <span>{tilesPlaced} / {state.bag.length}</span>
            </div>
            <div className="hm-status-row">
              <span>Time</span>
              <span>{formatTime(elapsed)}</span>
            </div>
            {meta.avgSolveMs != null && (
              <div className="hm-status-row">
                <span><span className="hm-status-long">Avg solve</span><span className="hm-status-short">Avg</span></span>
                <span>{formatTime(meta.avgSolveMs)}</span>
              </div>
            )}
            <div className="hm-status-row">
              <span>Solves</span>
              <span>{meta.playCount}</span>
            </div>
            {meta.viewCount != null && meta.viewCount > 0 && (
              <div className="hm-status-row" title={`${meta.playCount} solves across ${meta.viewCount} views`}>
                <span><span className="hm-status-long">Completion</span><span className="hm-status-short">Done</span></span>
                <span>{Math.min(100, Math.round((meta.playCount / meta.viewCount) * 100))}%</span>
              </div>
            )}
          </div>

          <div className="hm-divider" />

          <div className="hm-rail-actions">
            <div className="hm-eyebrow">Actions</div>
            <div className="hm-actions">
              <button
                className="hm-action hm-action-mobile-hide"
                onClick={() => dispatch({ type: 'UNDO' })}
                disabled={state.history.length === 0 || state.solved}
              >Undo</button>
              <button
                className="hm-action"
                onClick={() => {
                  if (state.history.length > 0 && !confirm('Reset this puzzle? Your placements will be cleared.')) return;
                  // RESET's daily-puzzle path would regenerate from
                  // dailyPuzzle('Easy', ...) — wrong for a custom map.
                  // RESTORE swaps state wholesale; build a fresh one.
                  try { localStorage.removeItem(startedAtKey(id)); } catch { /* ignore */ }
                  setFocusedCellIdx(null);
                  dispatch({ type: 'RESTORE', snapshot: makeStateFor(meta) });
                  setNow(Date.now());
                  solveSentRef.current = false;
                  setWinDismissed(false);
                  if (auth.user) {
                    lastSavedRef.current = '';
                    clearCustomProgress(id).catch(() => { /* fire-and-forget */ });
                  }
                }}
              >Reset</button>
              <button
                className="hm-action"
                onClick={() => setShowLeaderboard(true)}
              >Leaderboard</button>
            </div>
          </div>

          <div className="hm-divider" />

          <div className="hm-rail-marks">
            <div className="hm-eyebrow">Marks</div>
            <MarkLegend />
          </div>
        </aside>

        <main className="hm-board-area" style={{ position: 'relative' }}>
          <div className="hm-custom-meta">
            <div className="hm-custom-title">{meta.payload.name}</div>
            <div className="hm-custom-actions">
              <button
                type="button"
                className={`hm-custom-vote ${myVote === 'up' ? 'active' : ''}`}
                onClick={() => handleVote('up')}
                title={!auth.user ? 'Sign in to vote' : myVote === 'up' ? 'Click to remove your thumbs up' : 'Thumbs up'}
                aria-pressed={myVote === 'up'}
              >👍 <span>{thumbsUp}</span></button>
              <button
                type="button"
                className={`hm-custom-vote ${myVote === 'down' ? 'active' : ''}`}
                onClick={() => handleVote('down')}
                title={!auth.user ? 'Sign in to vote' : myVote === 'down' ? 'Click to remove your thumbs down' : 'Thumbs down'}
                aria-pressed={myVote === 'down'}
              >👎 <span>{thumbsDown}</span></button>
              <button
                type="button"
                className="hm-custom-actbtn hm-custom-actbtn-muted"
                onClick={() => {
                  if (!auth.user) { alert('Sign in to report a puzzle.'); return; }
                  setShowReport(true);
                }}
                title="Report this puzzle"
              >Report</button>
              {meta.isMine && (
                <button
                  type="button"
                  className="hm-custom-actbtn hm-custom-actbtn-danger"
                  onClick={async () => {
                    if (!confirm('Delete this puzzle? The share link will stop working immediately. This can\'t be undone.')) return;
                    const r = await deleteCustomPuzzle(id);
                    if (r.ok) {
                      window.location.href = '/browse';
                    } else {
                      alert(r.error === 'forbidden'
                        ? 'You can only delete puzzles you authored.'
                        : "Couldn't delete the puzzle. Try again in a moment.");
                    }
                  }}
                  title="Delete this puzzle"
                >Delete</button>
              )}
            </div>
          </div>

          <div className="hm-board-wrap" ref={boardWrapRef}>
            <HexBoard
              cells={state.cells}
              evaluatedConstraints={evaluatedConstraints}
              badRestr={badRestr}
              R={boardR}
              pad={boardPad}
              gap={5}
              onCellClick={handleCellClick}
              onCellPointerDown={handleCellPointerDown}
              clickableCells={clickableCells}
              draggableCells={draggableCells}
              recentlyPlaced={state.recentlyPlaced}
              drag={drag}
              dragOverIdx={drag && drag.active ? drag.overIdx : null}
              focusedCellIdx={focusedCellIdx}
            />
          </div>

          <div className="hm-bag-wrap">
            <Bag
              tiles={state.bag}
              selectedId={state.selectedTileId}
              onSelectTile={handleSelectTile}
              onTilePointerDown={handleTilePointerDown}
              draggingTileId={drag && drag.active && drag.type === 'bag' ? (drag.tileId ?? null) : null}
              highlightedIds={highlightedTileIds}
              isReturnTarget={!!drag && drag.active && drag.type === 'cell' && !!drag.overBag}
            />
          </div>
        </main>
      </div>

      {drag && drag.active && drag.value != null && (
        <div
          className="hm-drag-ghost"
          style={{ transform: `translate3d(${drag.x}px, ${drag.y}px, 0) translate(-50%, -50%)` }}
        >
          <BagTile value={drag.value} size={Math.max(36, boardR * 1.4)} />
        </div>
      )}

      {/*
        Same suppression rule as the main App's WinModal: don't slide
        the win banner on top of a leaderboard / report modal that the
        player just opened. The banner returns when those close.
      */}
      {state.solved && !winDismissed && !showLeaderboard && !showReport && (
        <WinBanner
          id={id}
          time={state.elapsedAtSolve}
          name={meta.payload.name}
          onClose={() => setWinDismissed(true)}
          onShowLeaderboard={() => { setWinDismissed(true); setShowLeaderboard(true); }}
        />
      )}

      {showLeaderboard && (
        <LeaderboardModal id={id} puzzleName={meta.payload.name} onClose={() => setShowLeaderboard(false)} />
      )}
      {showReport && (
        <ReportModal id={id} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

function WinBanner({
  id, time, name, onClose, onShowLeaderboard,
}: {
  id: string;
  time: number;
  name: string;
  onClose: () => void;
  onShowLeaderboard: () => void;
}) {
  // Suppress unused-import lint warning — `id` is referenced via the
  // leaderboard handler so future iterations can show inline top
  // placements without restructuring.
  void id;
  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Puzzle solved">
      <div className="hm-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2>Solved.</h2>
        <p style={{ fontSize: 13, color: v('inkMute') }}>{name}</p>
        <div className="hm-win-stats">
          <div className="hm-win-stat">
            <div className="hm-win-stat-label">Time</div>
            <div className="hm-win-stat-value">{formatTime(time)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 18 }}>
          <button type="button" className="hm-action" onClick={onShowLeaderboard}>See leaderboard</button>
          <a href="/browse" className="hm-action" style={{ textDecoration: 'none' }}>Browse more</a>
          <button type="button" className="hm-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

function LeaderboardModal({
  id, puzzleName, onClose,
}: {
  id: string;
  puzzleName: string;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchLeaderboard(id, 25);
      if (cancelled) return;
      setEntries(data ?? []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Leaderboard">
      <div className="hm-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2>Leaderboard</h2>
        <p style={{ fontSize: 13, color: v('inkMute') }}>{puzzleName}</p>
        {loading ? (
          <p style={{ marginTop: 16, color: v('inkMute') }}>Loading…</p>
        ) : !entries || entries.length === 0 ? (
          <p style={{ marginTop: 16, color: v('inkMute') }}>No times posted yet — be the first.</p>
        ) : (
          <ol className="hm-leaderboard">
            {entries.map((e, i) => (
              <li key={`${e.displayName}-${e.solvedAt}-${i}`}>
                <span className="hm-leaderboard-rank">{i + 1}</span>
                <span className="hm-leaderboard-name">{e.displayName}</span>
                <span className="hm-leaderboard-time">{formatTime(e.elapsedMs)}</span>
              </li>
            ))}
          </ol>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="hm-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

function ReportModal({ id, onClose }: { id: string; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<'ok' | string | null>(null);

  async function submit() {
    setSubmitting(true);
    const r = await reportCustomPuzzle(id, reason.trim() || undefined);
    setSubmitting(false);
    if (r.ok) setOutcome('ok');
    else setOutcome(r.error ?? 'error');
  }

  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Report puzzle">
      <div className="hm-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        {outcome === 'ok' ? (
          <>
            <h2>Reported.</h2>
            <p style={{ fontSize: 14, color: v('inkSoft'), marginTop: 8 }}>
              Thanks — we'll review this puzzle. If it crosses the report threshold it'll be auto-hidden pending review.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="hm-primary" onClick={onClose}>Close</button>
            </div>
          </>
        ) : outcome ? (
          <>
            <h2>Couldn't report.</h2>
            <p style={{ fontSize: 14, color: v('inkSoft'), marginTop: 8 }}>
              {outcome === 'already_reviewed' && 'This puzzle has already been reviewed by a moderator.'}
              {outcome === 'cant_report_own' && "You can't report a puzzle you authored."}
              {!['already_reviewed', 'cant_report_own'].includes(outcome) && 'Something went wrong — try again later.'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="hm-primary" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <h2>Report this puzzle</h2>
            <p style={{ fontSize: 13, color: v('inkMute') }}>
              Reports are reviewed by moderators. Puzzles with enough recent reports are auto-hidden pending review.
            </p>
            <label style={{ display: 'block', fontSize: 12, color: v('inkMute'), marginTop: 14, marginBottom: 4 }}>
              Reason (optional)
            </label>
            <textarea
              className="hm-designer-input"
              rows={3}
              maxLength={200}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="What's wrong with this puzzle?"
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="hm-action" onClick={onClose}>Cancel</button>
              <button type="button" className="hm-primary" disabled={submitting} onClick={submit}>
                {submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
